'use client';
import { Accordion, AccordionItem } from '@heroui/react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React from 'react';
import { useIsMobile } from '@/app/hooks/use-is-mobile';
import useOfferComparisonStore from '../store/use-offer-comparison-store';
import { Benefit } from '../types/benefit-type';
import { useCustomizationContext } from '../customizations-provider';

type BenefitProps = {
  benefitDetails: Benefit[];
};
export default function Benefits({ benefitDetails }: BenefitProps) {
  const t = useTranslations('benefits');
  const isMobile = useIsMobile();
  const offer = useOfferComparisonStore((state) => state.offer);
  const c = useCustomizationContext();
  const logo = c('benefits.logo');
  const image = c('benefits.image');

  if (offer == null) return;

  return (
    <div className="bg-[var(--color-card-accent)] p-6 rounded-3.75 mb-6 md:mb-0 border border-[var(--color-border-default)]">
      <div className="flex flex-row gap-x-3 justify-between">
        <p
          data-testid="benefits-title"
          className="font-heading text-xl md:text-[28px] leading-8 md:leading-10"
        >
          {t('title')}
        </p>
        <Image
          data-testid="benefits-icon"
          src={logo.url}
          alt={logo.title}
          width={80}
          height={80}
          className="h-10 w-10 md:h-20 md:w-20"
        />
      </div>
      <Accordion className="pt-8 pb-4">
        {benefitDetails.map(
          (benefit, index) =>
            benefit.image && (
              <AccordionItem
                classNames={{
                  title: 'font-semibold',
                  indicator: 'data-[open=true]:-rotate-180',
                }}
                key={index}
                aria-label={benefit.headline}
                title={
                  <p data-testid={`${benefit.headline}-title`}>
                    {benefit.headline}
                  </p>
                }
                indicator={
                  <Image
                    data-testid={`${benefit.headline}-indicator`}
                    src="/images/down-arrow.svg"
                    alt="down arrow"
                    height={7.41}
                    width={12}
                    className="transition-transform"
                  />
                }
                startContent={
                  <Image
                    data-testid={`${benefit.headline}-icon`}
                    alt={`${benefit.image.title} icon`}
                    src={benefit.image.url}
                    width={40}
                    height={40}
                  />
                }
              >
                <p data-testid={`${benefit.headline}-content`}>
                  {renderRichText(benefit.body, t)}
                </p>
              </AccordionItem>
            )
        )}
      </Accordion>
      <p
        data-testid="benefits-info"
        className="pb-6 leading-[22px] md:leading-6.5 text-sm md:text-base"
      >
        {t('body')}
      </p>
      <Image
        data-testid="benefits-image"
        alt={image.title}
        src={image.url}
        width={isMobile ? 295 : 440}
        height={162}
        className="rounded-3.75 h-[162px] w-full"
      />
    </div>
  );
}

function renderRichText(text: string, t: (key: string) => string) {
  const [before, link, after] = text.split(/<link>|<\/link>/g);
  return (
    <div className="text-sm md:text-base">
      {before}
      <a
        href={t('priceSheet')}
        target="_blank"
        className="underline underline-offset-4"
      >
        {link}
      </a>
      {after}
    </div>
  );
}
